// src/components/NewCompanyEnrichmentModal.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Upload, FileText, FolderPlus } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import { API_BASE_URL } from '../constants';
import './NewCompanyEnrichmentModal.css';
import './list-selection-modal.css';

interface NewCompanyEnrichmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const NewCompanyEnrichmentModal: React.FC<NewCompanyEnrichmentModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const { showToast, addJobId, state } = useAppContext();
  const { makeRequest, getExportConfig, getSchema, getAccountLists, createAccountList } = useApi();
  
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'mapping' | 'enriching' | 'results' | 'list_selection' | 'unlocking' | 'exporting' | 'export_complete' | 'unlock_complete' | 'complete'>('upload');
  const [columnMapping, setColumnMapping] = useState<any>(null);
  const [userMapping, setUserMapping] = useState<{
    company_name_column: string | null;
    company_domain_column: string | null;
  }>({
    company_name_column: null,
    company_domain_column: null
  });
  const [uploadDetails, setUploadDetails] = useState<any>(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState<any>(null);
  const [unlockJobId, setUnlockJobId] = useState<string | null>(null);
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [finalResults, setFinalResults] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<any>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [enrichmentName, setEnrichmentName] = useState<string>('');
  
  // Field selection state for export step
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [fieldGroups, setFieldGroups] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [defaultFields, setDefaultFields] = useState<string[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  
  // Account list selection state
  const [accountLists, setAccountLists] = useState<any[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
  const [loadingLists, setLoadingLists] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load field schema when export step is reached
  const loadFieldSchema = useCallback(async () => {
    setLoadingFields(true);
    try {
      const [schemaData, exportConfigData] = await Promise.all([
        getSchema('company'),
        getExportConfig()
      ]);
      
      if (schemaData) {
        // Only show company fields that are available for company enrichment
        const companySearchFields = [
          "COMPANY_ID",
          "COMPANY_NAME", 
          "COMPANY_DOMAIN",
          "COMPANY_CITY",
          "COMPANY_STATE",
          "COMPANY_COUNTRY",
          "COMPANY_LOGO_URL"
        ];
        
        
        const groups: any[] = [];
        
        // Create Company group with only the available company fields
        const companyFields = companySearchFields
          .map(fieldName => {
            const definition = schemaData.field_definitions?.[fieldName];
            return definition ? { name: fieldName, definition } : null;
          })
          .filter(Boolean);
        
        if (companyFields.length > 0) {
          groups.push({
            sectionName: 'Company',
            fields: companyFields
          });
        }
        
        setFieldGroups(groups);
        setRenderKey(prev => prev + 1); // Force re-render
      }
      
      // Set company-specific default fields (only COMPANY_NAME and COMPANY_DOMAIN)
      const companyDefaultFields = ['COMPANY_NAME', 'COMPANY_DOMAIN'];
      setDefaultFields(companyDefaultFields);
      // Set default selected fields
      setSelectedFields(new Set(companyDefaultFields));
    } catch (error) {
      console.error('Failed to load schema or export config:', error);
    } finally {
      setLoadingFields(false);
    }
  }, [getSchema, getExportConfig]);

  // Load field schema when export step is reached
  useEffect(() => {
    if (currentStep === 'exporting') {
      loadFieldSchema();
      setSearchTerm('');
    }
  }, [currentStep, loadFieldSchema]);

  // Debug fieldGroups changes
  useEffect(() => {
    // Field groups state tracking
  }, [fieldGroups, currentStep]);

  // Poll for unlock job completion
  useEffect(() => {
    if (currentStep === 'unlocking' && unlockJobId && state.activeJobs) {
      const job = state.activeJobs.find(j => j.job_id === unlockJobId);
      if (job) {
        if (job.status === 'completed') {
          setCurrentStep('exporting');
          showToast({
            type: 'success',
            title: 'Unlock Complete',
            message: 'Companies unlocked successfully!'
          });
        } else if (job.status === 'failed') {
          showToast({
            type: 'error',
            title: 'Unlock Failed',
            message: job.error || 'Unlock process failed'
          });
          setCurrentStep('results');
        }
      }
    }
  }, [currentStep, unlockJobId, state.activeJobs, showToast]);

  // Poll for export job completion
  useEffect(() => {
    if (currentStep === 'exporting' && exportJobId && state.activeJobs) {
      const job = state.activeJobs.find(j => j.job_id === exportJobId);
      if (job) {
        if (job.status === 'completed') {
          setFinalResults(job.result);
          setCurrentStep('export_complete');
          showToast({
            type: 'success',
            title: 'Export Complete',
            message: `Successfully exported ${job.result?.record_count || 0} enriched records with source data`
          });
          
          // Don't auto-download - let user click the download button
        } else if (job.status === 'failed') {
          showToast({
            type: 'error',
            title: 'Export Failed',
            message: job.error || 'Export process failed'
          });
          setCurrentStep('exporting');
        }
      }
    }
  }, [currentStep, exportJobId, state.activeJobs, showToast]);

  const resetModal = useCallback(() => {
    setUploadId(null);
    setCurrentStep('upload');
    setColumnMapping(null);
    setUserMapping({
      company_name_column: null,
      company_domain_column: null
    });
    setUploadDetails(null);
    setEnrichmentStatus(null);
    setUnlockJobId(null);
    setExportJobId(null);
    setFinalResults(null);
    setSearchResults(null);
    setCsvFile(null);
    setSelectedFields(new Set());
    setFormat('csv');
    setAccountLists([]);
    setSelectedListIds(new Set());
    setLoadingLists(false);
    setShowCreateList(false);
    setNewListName('');
  }, []);

  const handleClose = useCallback(() => {
    resetModal();
    onClose();
  }, [resetModal, onClose]);

  // Load account lists
  const loadAccountLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const data = await getAccountLists(true) as { success: boolean; lists?: any[] };
      if (data?.success) {
        setAccountLists(data.lists || []);
      } else {
        throw new Error('Failed to load account lists');
      }
    } catch (error) {
      console.error('Failed to load account lists:', error);
      setAccountLists([]);
      showToast({
        type: 'error',
        title: 'Error Loading Lists',
        message: 'Failed to load account lists'
      });
    } finally {
      setLoadingLists(false);
    }
  }, [getAccountLists, showToast]);


  const proceedToMatches = () => {
    setCurrentStep('list_selection');
    loadAccountLists();
  };

  const startUnlockProcess = async () => {
    if (!enrichmentStatus?.enrichment_id) {
      showToast({
        type: 'error',
        title: 'Error',
        message: 'No enrichment ID available'
      });
      return;
    }

    if (selectedListIds.size === 0) {
      showToast({
        type: 'error',
        title: 'No Lists Selected',
        message: 'Please select at least one account list to add the companies to'
      });
      return;
    }

    setCurrentStep('unlocking');

    try {
      // Start unlock job
      const response = await makeRequest(`${API_BASE_URL}/api/enrichment/source/csv/companies/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enrichment_id: enrichmentStatus.enrichment_id,
          list_ids: Array.from(selectedListIds)
        })
      });

      if (response?.data && (response.data as any).job_id) {
        setUnlockJobId((response.data as any).job_id);
        addJobId((response.data as any).job_id);
        // Poll for unlock completion
        pollUnlockStatus((response.data as any).job_id);
      } else {
        throw new Error('No job ID returned from unlock request');
      }
    } catch (error) {
      console.error('Failed to start unlock process:', error);
      showToast({
        type: 'error',
        title: 'Unlock Failed',
        message: 'Failed to start unlock process'
      });
      setCurrentStep('results');
    }
  };

  const pollUnlockStatus = async (jobId: string) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await makeRequest(`${API_BASE_URL}/api/jobs/${jobId}`, {
          method: 'GET'
        });
        
        if (response?.data) {
          const job = response.data as any;

          if (job.status === 'completed') {
            setCurrentStep('exporting');
            showToast({
              type: 'success',
              title: 'Unlock Complete',
              message: 'Companies unlocked successfully!'
            });
          } else if (job.status === 'failed') {
            showToast({
              type: 'error',
              title: 'Unlock Failed',
              message: job.error || 'Unlock process failed'
            });
            setCurrentStep('results');
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            showToast({
              type: 'error',
              title: 'Unlock Timeout',
              message: 'Unlock process timed out'
            });
            setCurrentStep('results');
          }
        } else {
          throw new Error('No response data');
        }
      } catch (error) {
        console.error('Failed to poll unlock status:', error);
        showToast({
          type: 'error',
          title: 'Unlock Error',
          message: 'Failed to check unlock status'
        });
        setCurrentStep('results');
      }
    };

    poll();
  };

  const startExportProcess = async () => {
    if (!enrichmentStatus?.enrichment_id) {
      showToast({
        type: 'error',
        title: 'Error',
        message: 'No enrichment ID available'
      });
      return;
    }

    setCurrentStep('exporting');

    try {
      // Start export job
      const selectedFieldsArray = Array.from(selectedFields);
      
      const response = await makeRequest(`${API_BASE_URL}/api/enrichment/destination/csv/companies/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_id: uploadDetails?.upload_id,
          enrichment_id: enrichmentStatus.enrichment_id,
          export_fields: selectedFieldsArray,
          format: format
        })
      });

      if (response?.data && (response.data as any).job_id) {
        setExportJobId((response.data as any).job_id);
        addJobId((response.data as any).job_id);
        // Poll for export completion
        pollExportStatus((response.data as any).job_id);
      } else {
        throw new Error('No job ID returned from export request');
      }
    } catch (error) {
      console.error('Failed to start export process:', error);
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to start export process'
      });
      setCurrentStep('unlock_complete');
    }
  };

  const pollExportStatus = async (jobId: string) => {
    const maxAttempts = 30;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await makeRequest(`${API_BASE_URL}/api/jobs/${jobId}`, {
          method: 'GET'
        });
        
        if (response?.data) {
          const job = response.data as any;

          if (job.status === 'completed') {
            setFinalResults(job.result);
            setCurrentStep('export_complete');
          } else if (job.status === 'failed') {
            showToast({
              type: 'error',
              title: 'Export Failed',
              message: job.error || 'Export process failed'
            });
            setCurrentStep('exporting');
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            showToast({
              type: 'error',
              title: 'Export Timeout',
              message: 'Export process timed out'
            });
            setCurrentStep('exporting');
          }
        } else {
          throw new Error('No response data');
        }
      } catch (error) {
        console.error('Failed to poll export status:', error);
        showToast({
          type: 'error',
          title: 'Export Error',
          message: 'Failed to check export status'
        });
        setCurrentStep('unlock_complete');
      }
    };

    poll();
  };


  const handleFileSelect = useCallback((file: File) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showToast({
        type: 'error',
        title: 'Invalid File Type',
        message: 'Please select a CSV file'
      });
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      showToast({
        type: 'error',
        title: 'File Too Large',
        message: 'File size must be less than 50MB'
      });
      return;
    }

    setCsvFile(file);
    if (!enrichmentName) {
      setEnrichmentName(file.name.replace('.csv', ''));
    }
  }, [showToast]);

  const uploadFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('enrichment_name', `Company Enrichment - ${new Date().toLocaleDateString()}`);

      const response = await makeRequest(
        `${API_BASE_URL}/api/enrichment/source/csv/companies/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );
      

      if (response?.data && (response.data as any).upload_id) {
        const data = response.data as any;
        setUploadId(data.upload_id);
        
        // Move to mapping step
        setCurrentStep('mapping');
        showToast({
          type: 'success',
          title: 'Upload Successful',
          message: 'File uploaded successfully'
        });
        
        // Load upload details to get headers and column mapping
        await loadUploadDetails(data.upload_id);
      } else {
        console.error('No upload_id in response:', response);
        showToast({
          type: 'error',
          title: 'Upload Failed',
          message: 'No upload ID received from server'
        });
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      showToast({
        type: 'error',
        title: 'Upload Failed',
        message: error.message || 'Upload failed'
      });
    }
  };


  const startEnrichment = async () => {
    if (!uploadId || !userMapping.company_name_column || !userMapping.company_domain_column) {
      showToast({
        type: 'error',
        title: 'Missing Columns',
        message: 'Please select both company name and domain columns'
      });
      return;
    }

    setCurrentStep('enriching');

    try {
      const response = await makeRequest(
        `${API_BASE_URL}/api/enrichment/source/csv/companies/search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            upload_id: uploadId,
            enrichment_name: `Company Enrichment - ${new Date().toLocaleDateString()}`,
            include_uuid_list: true,
            column_mapping: userMapping
          }),
        }
      );


      if (response?.data && (response.data as any).success) {
        const enrichmentResults = response.data as any;
        setEnrichmentStatus(enrichmentResults);
        setSearchResults(enrichmentResults);
        setCurrentStep('results');
        showToast({
          type: 'success',
          title: 'Search Completed',
          message: `Found ${enrichmentResults.company_ids?.length || 0} matches. Ready to proceed to export.`
        });
      } else {
        throw new Error((response?.data as any)?.message || 'Enrichment failed');
      }
    } catch (error: any) {
      console.error('Enrichment error:', error);
      showToast({
        type: 'error',
        title: 'Enrichment Failed',
        message: error.message || 'Failed to enrich company data'
      });
      setCurrentStep('mapping');
    }
  };


  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleUpload = () => {
    if (csvFile) {
      uploadFile(csvFile);
    }
  };

  const handleSearch = () => {
    setCurrentStep('enriching');
    startEnrichment();
  };

  const handleExport = () => {
    startExportProcess();
  };

  const handleComplete = () => {
    setCurrentStep('complete');
    onComplete();
  };

  // Load upload details and column mapping (same as contact enrichment)
  const loadUploadDetails = useCallback(async (uploadId: string) => {
    try {
      const response = await makeRequest<{
        upload_id: string;
        file_name: string;
        headers: string[];
        column_mapping: any;
        enrichment_status: string;
      }>(`${API_BASE_URL}/api/enrichment/source/csv/companies/upload/${uploadId}`, {
        method: 'GET'
      });

      if (response?.data) {
        setUploadDetails(response.data);
        setColumnMapping(response.data.column_mapping);
        
        // Auto-detect company name and domain columns
        const headers = response.data.headers || [];
        const detectedMapping = {
          company_name_column: headers.find(h => 
            h.toLowerCase().includes('company') && h.toLowerCase().includes('name')
          ) || headers.find(h => 
            h.toLowerCase().includes('company')
          ) || headers[0],
          company_domain_column: headers.find(h => 
            h.toLowerCase().includes('domain') || h.toLowerCase().includes('website')
          ) || headers.find(h => 
            h.toLowerCase().includes('url') || h.toLowerCase().includes('site')
          ) || headers[1]
        };

        setUserMapping(detectedMapping);
      }
    } catch (error) {
      console.error('Failed to load upload details:', error);
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load file details'
      });
    }
  }, [makeRequest, showToast]);

  if (!isOpen) return null;

  const renderStepContent = () => {
    
    if (currentStep === 'upload') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Upload CSV File</h3>
            <p>Select a CSV file containing company information to enrich</p>
          </div>
          
          <div className="form-group">
            <label htmlFor="enrichment-name">Enrichment Name</label>
            <input
              id="enrichment-name"
              type="text"
              value={enrichmentName}
              onChange={(e) => setEnrichmentName(e.target.value)}
              placeholder="Enter a name for this enrichment"
              className="form-input"
            />
          </div>

          <div className="file-upload-area" 
               onDragOver={handleDragOver}
               onDragLeave={handleDragLeave}
               onDrop={handleDrop}
               onClick={() => fileInputRef.current?.click()}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => handleFileSelect(e.target.files?.[0]!)}
              style={{ display: 'none' }}
            />
            {csvFile ? (
              <div className="file-selected">
                <FileText size={24} />
                <span>{csvFile.name}</span>
                <span className="file-size">
                  {(csvFile.size / 1024).toFixed(1)} KB
                </span>
              </div>
            ) : (
              <div className="file-upload-placeholder">
                <Upload size={32} />
                <span>Click to select CSV file</span>
                <span className="file-upload-hint">or drag and drop</span>
              </div>
            )}
          </div>

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={handleUpload}
              disabled={!csvFile || !enrichmentName || false}
            >
              Upload CSV
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'mapping') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Review Column Mapping</h3>
            <p>Review the detected column mapping for your CSV file</p>
          </div>

          {!columnMapping ? (
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <p>Loading column mapping...</p>
            </div>
          ) : (
            <>
              <div className="mapping-details">
            <div className="file-info">
              <h4>File: {uploadDetails?.file_name}</h4>
              <p>Headers: {uploadDetails?.headers?.length || 0} columns detected</p>
            </div>

                {columnMapping && uploadDetails?.headers && (
                  <div className="column-mapping">
                    <h4>Column Mapping:</h4>
                    <p className="mapping-description">Select which columns from your CSV file should be used for each field:</p>
                    <div className="mapping-items">
                      <div className="mapping-item">
                        <label htmlFor="company-name-mapping">
                          <strong>Company Name:</strong>
                        </label>
                        <select
                          id="company-name-mapping"
                          value={userMapping.company_name_column || ''}
                          onChange={(e) => setUserMapping(prev => ({ ...prev, company_name_column: e.target.value }))}
                          className="mapping-select"
                        >
                          <option value="">No mapping</option>
                          {uploadDetails.headers.map((header: string) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mapping-item">
                        <label htmlFor="company-domain-mapping">
                          <strong>Company Domain:</strong>
                        </label>
                        <select
                          id="company-domain-mapping"
                          value={userMapping.company_domain_column || ''}
                          onChange={(e) => setUserMapping(prev => ({ ...prev, company_domain_column: e.target.value }))}
                          className="mapping-select"
                        >
                          <option value="">No mapping</option>
                          {uploadDetails.headers.map((header: string) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="button-secondary"
                  onClick={() => setCurrentStep('upload')}
                >
                  Back to Upload
                </button>
            <button
              className="button-primary"
              onClick={handleSearch}
              disabled={!userMapping.company_name_column && !userMapping.company_domain_column}
            >
              Search for Matches
            </button>
              </div>
            </>
          )}
        </div>
      );
    }

    if (currentStep === 'enriching') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Enriching Your Data</h3>
            <p>Searching for matching companies in our database...</p>
          </div>

          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Processing your file...</p>
            <p className="processing-note">
              This may take a few minutes depending on the size of your file.
            </p>
          </div>
        </div>
      );
    }


    if (currentStep === 'results') {
      const matchCount = searchResults?.company_ids?.length || 0;
      const stats = searchResults?.stats || {};
      const totalRows = stats.total_csv_rows || 0;
      const matchRate = stats.match_rate_percent || 0;
      
      // Calculate detailed match breakdown (similar to contact enrichment)
      const companyNameMatches = stats.company_name_matches || matchCount;
      const companyDomainMatches = stats.company_domain_matches || matchCount;
      const totalCompanyNames = stats.total_company_names || totalRows;
      const totalCompanyDomains = stats.total_company_domains || totalRows;
      
      return (
        <div className="step-content results-step">
          <div className="step-header">
            <h3>Search Results</h3>
            <p>We found companies in our database that match your CSV data.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Companies Found:</strong>
              <span>{matchCount}</span>
            </div>
            <div className="summary-item">
              <strong>Match Rate:</strong>
              <span>{matchRate.toFixed(1)}%</span>
            </div>
            <div className="summary-item">
              <strong>CSV Rows Processed:</strong>
              <span>{totalRows}</span>
            </div>
          </div>

          {/* Match breakdown */}
          <div className="completion-summary">
            <div className="summary-item">
              <strong>Company Name Matches:</strong>
              <span>{companyNameMatches}</span>
            </div>
            <div className="summary-item">
              <strong>Company Domain Matches:</strong>
              <span>{companyDomainMatches}</span>
            </div>
          </div>

          {/* Search criteria info */}
          <div className="completion-summary">
            <div className="summary-item">
              <strong>Search Criteria:</strong>
              <span>
                {[
                  totalCompanyNames > 0 && `${totalCompanyNames} company names`,
                  totalCompanyDomains > 0 && `${totalCompanyDomains} company domains`
                ].filter(Boolean).join(', ')}
              </span>
            </div>
          </div>

          <div className="step-actions">
            <button
              className="button-primary"
              onClick={proceedToMatches}
              disabled={!matchCount}
            >
              Unlock {matchCount} Matches
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'list_selection') {
      const matchCount = searchResults?.company_ids?.length || 0;
      
      return (
        <div className="step-content">
          <div className="modal-header">
            <h2>Unlock Companies</h2>
            <button className="modal-close" onClick={() => setCurrentStep('results')}>
              <X size={20} />
            </button>
          </div>

          <div className="modal-body">
            <div className="selection-summary">
              <span>Adding {matchCount} compan{matchCount !== 1 ? 'ies' : 'y'} to:</span>
            </div>

            {/* Create New List Section */}
            <div className="create-list-section">
              <button
                className={`create-list-toggle ${showCreateList ? 'active' : ''}`}
                onClick={() => setShowCreateList(!showCreateList)}
              >
                <FolderPlus size={16} />
                <span>Create New List</span>
              </button>
              
              {showCreateList && (
                <div className="create-list-form">
                  <div className="form-group">
                    <label htmlFor="list-name">List Name *</label>
                    <input
                      id="list-name"
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="Enter list name..."
                      maxLength={200}
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button
                      className="button-secondary"
                      onClick={() => {
                        setShowCreateList(false);
                        setNewListName('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="button-primary"
                      onClick={async () => {
                        if (!newListName.trim()) {
                          showToast({
                            type: 'error',
                            title: 'Invalid Name',
                            message: 'Please enter a list name'
                          });
                          return;
                        }
                        
                        try {
                          const response = await createAccountList(newListName.trim(), `List created from company enrichment: ${enrichmentName}`) as { list_id?: string };
                          if (response?.list_id) {
                            setSelectedListIds(prev => new Set([...prev, response.list_id!]));
                            setShowCreateList(false);
                            setNewListName('');
                            loadAccountLists(); // Refresh the list
                            showToast({
                              type: 'success',
                              title: 'List Created',
                              message: `Created list "${newListName.trim()}" and selected it`
                            });
                          } else {
                            throw new Error('Failed to create list');
                          }
                        } catch (error) {
                          console.error('Failed to create list:', error);
                          showToast({
                            type: 'error',
                            title: 'Create Failed',
                            message: 'Failed to create new list'
                          });
                        }
                      }}
                      disabled={!newListName.trim()}
                    >
                      Create
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Existing Lists Section */}
            <div className="existing-lists-section">
              <h3>Existing Account Lists</h3>
              
              {loadingLists ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading account lists...</p>
                </div>
              ) : accountLists.length === 0 ? (
                <div className="empty-state">
                  <p>No account lists found. Create one above.</p>
                </div>
              ) : (
                <div className="lists-grid">
                  {accountLists.map((list) => (
                    <div
                      key={list.list_id}
                      className={`list-item ${selectedListIds.has(list.list_id) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedListIds(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(list.list_id)) {
                            newSet.delete(list.list_id);
                          } else {
                            newSet.add(list.list_id);
                          }
                          return newSet;
                        });
                      }}
                    >
                      <div className="list-item-header">
                        <div className="list-item-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedListIds.has(list.list_id)}
                            onChange={() => {
                              setSelectedListIds(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(list.list_id)) {
                                  newSet.delete(list.list_id);
                                } else {
                                  newSet.add(list.list_id);
                                }
                                return newSet;
                              });
                            }}
                          />
                          <div className="checkbox-visual">
                            {selectedListIds.has(list.list_id) && <div className="checkmark">✓</div>}
                          </div>
                        </div>
                        <div className="list-item-info">
                          <h4 className="list-name">{list.name}</h4>
                          <div className="list-meta">
                            <span className="contact-count">
                              {list.account_count || 0} compan{(list.account_count || 0) !== 1 ? 'ies' : 'y'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <div className="selection-info">
              {selectedListIds.size > 0 && (
                <span>{selectedListIds.size} list{selectedListIds.size !== 1 ? 's' : ''} selected</span>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="button-secondary"
                onClick={() => setCurrentStep('results')}
              >
                Back
              </button>
              <button
                className="button-primary"
                onClick={startUnlockProcess}
                disabled={selectedListIds.size === 0}
              >
                Unlock {matchCount} Compan{matchCount !== 1 ? 'ies' : 'y'}
              </button>
            </div>
          </div>
        </div>
      );
    }


    if (currentStep === 'unlocking') {
      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Unlocking Companies</h3>
            <p>Adding found companies to your list...</p>
          </div>

          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p>Unlocking companies...</p>
            <p className="processing-note">
              This process may take a few minutes.
            </p>
          </div>
        </div>
      );
    }



    if (currentStep === 'export_complete') {
      const contactCount = finalResults?.contactCount || searchResults?.company_ids?.length || enrichmentStatus?.company_ids?.length || 0;
      
      // Get download URL from finalResults (stored when export job completed)
      let downloadUrl = finalResults?.signed_download_url;
      
      // Try alternative ways to get the download URL
      if (!downloadUrl && finalResults) {
        downloadUrl = finalResults.download_url || finalResults.url || finalResults.file_url;
      }
      

      const handleDownload = () => {
        if (downloadUrl) {
          window.open(downloadUrl, '_blank');
        } else {
          showToast({
            type: 'error',
            title: 'Download Failed',
            message: 'Download URL not available'
          });
        }
      };

      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Export Complete!</h3>
            <p>Your enriched company data has been exported and is ready for download.</p>
          </div>

          <div className="completion-summary">
            <div className="summary-item">
              <strong>Companies Exported:</strong>
              <span>{contactCount}</span>
            </div>
            <div className="summary-item">
              <strong>Status:</strong>
              <span>✅ Export file created successfully</span>
            </div>
          </div>

          <div className="step-actions">
            <button 
              className="button-secondary" 
              onClick={handleClose}
            >
              Close
            </button>
            <button 
              className="button-primary" 
              onClick={handleDownload}
              disabled={!downloadUrl}
            >
              Download Export
            </button>
          </div>
        </div>
      );
    }

    if (currentStep === 'exporting') {
      
      const filteredGroups = fieldGroups.map(group => ({
        ...group,
        fields: group.fields.filter((field: any) => 
          field.definition.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          field.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(group => group.fields.length > 0);
      

      const handleFieldToggle = (fieldName: string) => {
        const newSelected = new Set(selectedFields);
        if (newSelected.has(fieldName)) {
          newSelected.delete(fieldName);
        } else {
          newSelected.add(fieldName);
        }
        setSelectedFields(newSelected);
      };

      const selectAllFields = () => {
        const allFields = new Set<string>();
        fieldGroups.forEach(group => {
          group.fields.forEach((field: any) => allFields.add(field.name));
        });
        setSelectedFields(allFields);
      };

      const selectDefaultFields = () => {
        setSelectedFields(new Set(defaultFields));
      };

      const clearAllFields = () => {
        setSelectedFields(new Set());
      };

      const handleExport = async () => {
        try {
          await startExportProcess();
        } catch (error) {
          console.error('Failed to start export job:', error);
          showToast({
            type: 'error',
            title: 'Export Failed',
            message: 'Failed to start export job'
          });
        }
      };

      return (
        <div className="step-content">
          <div className="step-header">
            <h3>Select Export Fields</h3>
            <p>Choose which fields to include in your export file.</p>
          </div>

          <div className="export-fields-content">
            {loadingFields ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <div className="spinner" />
                <p>Loading fields...</p>
              </div>
            ) : (
              <>
                {/* Format & Actions in one row */}
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                      <input
                        type="radio"
                        value="csv"
                        checked={format === 'csv'}
                        onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}
                      />
                      CSV
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                      <input
                        type="radio"
                        value="json"
                        checked={format === 'json'}
                        onChange={(e) => setFormat(e.target.value as 'csv' | 'json')}
                      />
                      JSON
                    </label>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={selectAllFields}
                      className="button-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={selectDefaultFields}
                      className="button-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Default
                    </button>
                    <button
                      type="button"
                      onClick={clearAllFields}
                      className="button-secondary"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Field Groups */}
                <div className="field-groups" key={`field-groups-${renderKey}`}>
                  
                  {filteredGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="field-group" style={{ marginBottom: '20px' }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#374151',
                        borderBottom: '1px solid #e5e7eb',
                        paddingBottom: '4px'
                      }}>
                        {group.sectionName}
                      </h4>
                      <div className="field-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {group.fields.map((field: any, fieldIndex: number) => (
                          <label
                            key={fieldIndex}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              backgroundColor: selectedFields.has(field.name) ? '#f0f9ff' : 'white',
                              transition: 'background-color 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFields.has(field.name)}
                              onChange={() => handleFieldToggle(field.name)}
                              style={{ margin: 0, width: '16px', height: '16px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500', marginBottom: '2px' }}>{field.definition.label}</div>
                              <div style={{ color: '#6b7280', fontSize: '12px' }}>{field.name}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="step-actions">
            <div className="selection-info" style={{ 
              fontSize: '14px', 
              color: '#6b7280', 
              fontWeight: '500',
              marginRight: 'auto'
            }}>
              <span>{selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''} selected</span>
            </div>
            <button 
              className="button-secondary" 
              onClick={() => setCurrentStep('unlocking')}
            >
              Back
            </button>
            <button
              className="button-primary"
              onClick={handleExport}
              disabled={selectedFields.size === 0 || loadingFields}
            >
              Export {selectedFields.size} Field{selectedFields.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      );
    }


    return null;
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className={`modal-content ${currentStep === 'list_selection' ? 'list-selection-modal' : 'new-enrichment-modal'} ${currentStep === 'results' ? 'results-modal' : ''}`} key={renderKey} onClick={e => e.stopPropagation()}>
        {currentStep !== 'list_selection' && (
          <div className="modal-header">
            <h2>New Company Enrichment</h2>
            <button className="modal-close" onClick={handleClose}>
              <X size={20} />
            </button>
          </div>
        )}

        <div className="modal-body">
          {/* Step Content */}
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default NewCompanyEnrichmentModal;